/**
 * Validates a user-supplied redirect target (e.g. the ?next= or ?redirect=
 * query param on /login) and returns a same-origin path, or null.
 *
 * String-prefix checks alone are open redirects:
 *  - '/\\evil.com'  — browsers parse backslash as slash → '//evil.com'
 *  - '/.//evil.com' — WHATWG path normalization folds './' → pathname
 *    '//evil.com', which reads as protocol-relative when re-assigned to
 *    window.location.href
 *
 * So we reject backslashes, resolve against the current origin, require the
 * resolved origin to match, and reject any normalized pathname that still
 * begins with '//'.
 */
export function getSafeRedirectPath(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) {
    return null;
  }
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const resolved = new URL(raw, window.location.origin);
    if (resolved.origin !== window.location.origin) {
      return null;
    }
    if (resolved.pathname.startsWith('//')) {
      return null;
    }
    return resolved.pathname + resolved.search;
  } catch {
    return null;
  }
}

/**
 * Server-safe variant of {@link getSafeRedirectPath} for validating a caller-
 * supplied internal return path (e.g. the `returnTo` on a Stripe checkout call)
 * WITHOUT a browser `window`. We can't resolve against `window.location.origin`
 * on the server, so we validate the SHAPE and require an absolute in-app path:
 * it must start with a single `/`, contain no protocol/host, no backslash, no
 * '//' anywhere, and no control chars. The caller prepends its own trusted
 * origin, so a shape-valid path can only ever produce a same-origin URL. Query
 * strings are preserved; a hostname, scheme, or protocol-relative form → null.
 *
 * This is the open-redirect guard on the payment-return flow: an attacker-
 * supplied `returnTo` must never send a post-checkout user off-origin.
 */
export function sanitizeInternalPath(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const path = raw.trim();
  if (!path.startsWith('/')) return null;        // must be an absolute in-app path
  if (path.startsWith('//')) return null;        // protocol-relative → off-origin
  if (path.includes('\\')) return null;          // backslash parses as slash in browsers
  if (/[\x00-\x1f\x7f]/.test(path)) return null; // control chars
  if (/^\/[^/]*:/.test(path.slice(0, 12))) return null; // e.g. '/a:b' scheme-ish smell
  // Reject anything that isn't a plain path[?query][#hash]; a bare host can't
  // appear because we required a leading single '/'. Final belt: no whitespace.
  if (/\s/.test(path)) return null;
  return path;
}

/**
 * The request header the middleware stamps with the requested
 * `pathname + search` (C5 second review). Server layouts have no other way
 * to learn the URL they are gating — `headers()` carries no path on a hard
 * load — so `app/settings/layout.tsx` reads this to send an unauthenticated
 * visitor to `/login?next=<here>` instead of a bare `/login` that drops the
 * emailed `?connect=phone`. The middleware always OVERWRITES it, so a value a
 * client sends itself never reaches a layout; readers still run it through
 * {@link loginPathWithNext} (⇒ `sanitizeInternalPath`) before redirecting.
 */
export const REQUEST_PATH_HEADER = 'x-request-path';

/**
 * `/login?next=<encoded in-app path>` when `raw` is a shape-valid internal
 * path, else the bare `/login` — the ONE builder for a login bounce that
 * should return the person to where they were. `/login` applies
 * {@link getSafeRedirectPath} to `?next=` on the way back, so the round trip
 * is same-origin by construction.
 */
export function loginPathWithNext(raw: string | null | undefined): string {
  const next = sanitizeInternalPath(raw);
  return next ? `/login?next=${encodeURIComponent(next)}` : '/login';
}

/**
 * Append a cache-busting `auth_ts` to an in-app destination WITHOUT breaking a
 * query it already carries: `/settings/channels?connect=phone` must stay
 * `…?connect=phone&auth_ts=…`, never `…?connect=phone?auth_ts=…` (whose
 * `connect` value would read `phone?auth_ts=…`). Pure.
 */
export function withReloadParam(destination: string, ts: number): string {
  const sep = destination.includes('?') ? '&' : '?';
  return `${destination}${sep}auth_ts=${ts}`;
}

/**
 * The same-origin guard the in-app notification bell applies before following
 * a builder-written destination: a relative path or an absolute URL on
 * `origin` yields its path+search; anything else (another host, garbage)
 * yields `fallback`. Pure.
 */
export function sameOriginTarget(cta: unknown, fallback: string, origin: string): string {
  if (typeof cta !== 'string' || !cta) return fallback;
  try {
    const url = new URL(cta, origin);
    if (url.origin === origin) return url.pathname + url.search;
  } catch {
    // keep fallback
  }
  return fallback;
}
